import React, { useEffect, useId, useRef, useState } from 'react';
import captchaService from '../services/captchaService';
import './CaptchaWidget.css';

interface CaptchaWidgetProps {
  siteKey: string;
  onTokenChange: (token: string | null) => void;
  onReadyChange?: (ready: boolean) => void;
  onError?: (error: string) => void;
  className?: string;
}

const CaptchaWidget: React.FC<CaptchaWidgetProps> = ({
  siteKey,
  onTokenChange,
  onReadyChange,
  onError,
  className = ''
}) => {
  const reactId = useId().replace(/:/g, '');
  const containerId = `captcha-container-${reactId}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(siteKey));
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    onReadyChange?.(false);
    if (!siteKey) {
      setLoading(false);
      setIsRendered(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const mount = async () => {
      setLoading(true);
      setError(null);
      setIsRendered(false);
      onTokenChange(null);

      try {
        await captchaService.loadCaptchaScript(true);
        if (cancelled || !containerRef.current) return;

        captchaService.setTokenListener((token) => {
          if (!cancelled) onTokenChange(token);
        });

        containerRef.current.id = containerId;
        containerRef.current.innerHTML = '';

        await captchaService.renderCaptcha(containerId, siteKey);
        if (cancelled) return;

        setIsRendered(true);
        setLoading(false);
        onReadyChange?.(true);
      } catch (err) {
        if (cancelled) return;
        const errorMsg = err instanceof Error ? err.message : 'Failed to load CAPTCHA';
        setError(errorMsg);
        setLoading(false);
        setIsRendered(false);
        onReadyChange?.(false);
        onError?.(errorMsg);
      }
    };

    void mount();

    return () => {
      cancelled = true;
      captchaService.setTokenListener(null);
      onReadyChange?.(false);
    };
  }, [siteKey, containerId, retryCount, onTokenChange, onReadyChange, onError]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className={`captcha-widget ${className}`}>
      {error && (
        <div className="captcha-error text-red-600 text-sm mb-2">
          {error}
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="ml-2 text-blue-600 hover:text-blue-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        id={containerId}
        className="captcha-container"
        style={{ minHeight: isRendered ? undefined : '78px' }}
      />

      {loading && !error && (
        <div className="captcha-loading text-gray-500 text-sm">
          Loading CAPTCHA...
        </div>
      )}
    </div>
  );
};

export default CaptchaWidget;
